<script>
import { mapActions } from "vuex";
import { formatRelativeDate, formatHashrate } from "@/utils";
import AlertDescription from "@/pages/Alerts/Alert/Description";

export default {
  // Included components
  components: {
    AlertDescription,
  },

  props: {
    // The id of the group whose events should be shown. If groupId is
    // set, alertId should not be and vice versa.
    groupId: {
      type: String,
      default: null,
    },

    // The id of the alert whose events should be shown.
    alertId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      // The list of alertEvents being shown, or null when initially loading
      alertEvents: null,

      // The number of events to fetch next, 5, then 45, then 50, 100, etc.
      limit: 5,

      // The number of events loaded so far, used to get subsequent pages.
      offset: 0,

      // Whether there are more events not yet loaded.
      hasMoreEvents: false,
    };
  },

  computed: {
    // Compute the fields for the table, only excluding the "Alert"
    // column if the component specifies an alertId
    fields() {
      let fields = [
        {
          key: "description",
          label: "Description",
        },
        {
          key: "triggered",
          label: "Triggered",
          formatter(value) {
            return formatRelativeDate(value);
          },
          thStyle: { width: "20%" },
        },
      ];
      // If the component is showing alert events for a group or team,
      // show the "Alert" column.
      if (this.alertId === null) {
        fields.push({
          key: "alert.name",
          label: "Alert",
          thStyle: { width: "30%" },
        });
      }
      return fields;
    },
  },

  // When the component is loaded fetch the alert events
  async created() {
    this.fetchEvents();
  },

  methods: {
    // Handler for when the user clicks the "show more button"
    // Initially, the view shows 5, then 50, then 50 more each
    // time.
    async showMore() {
      if (this.limit === 5) {
        this.limit = 45;
        this.offset = 5;
      } else if (this.limit === 45) {
        this.limit = 50;
        this.offset = 5;
      } else {
        this.offset += 50;
      }
      this.fetchEvents();
    },

    // Fetch the alert events from the API.
    async fetchEvents() {
      // Fetch the events.
      let result = await this.getAlertEvents({
        alertId: this.alertId,
        groupId: this.groupId,
        limit: this.limit,
        offset: this.offset,
      });

      // If this is the first load, replace `this.alertEvents` with
      // the retrieved values. Otherwise, append to the existing list.
      if (this.alertEvents === null) {
        this.alertEvents = result.events;
      } else {
        this.alertEvents = this.alertEvents.concat(result.events);
      }

      // Update whether or not there are more entries to show.
      this.hasMoreEvents = result.paging.next_page !== null;
    },

    // Other helper functions and actions
    formatRelativeDate,
    formatHashrate,
    ...mapActions(["getAlertEvents"]),
  },
};
</script>

<template>
  <div>
    <b-row>
      <b-col>
        <b-card
          class="text-center py-5"
          v-if="alertEvents !== null && alertEvents.length === 0"
        >
          <b-card-title>No events</b-card-title>
          <b-card-text v-if="alertId === null">
            None of the configured alerts has triggered yet
          </b-card-text>
          <b-card-text v-else>This alert has not triggered yet</b-card-text>
        </b-card>
        <b-card
          class="Card-table"
          v-if="alertEvents !== null && alertEvents.length !== 0"
        >
          <b-table responsive small :fields="fields" :items="alertEvents">
            <template #cell(description)="{ item }">
              <AlertDescription :alert="item.alert" :event="item" />
            </template>
            <template #cell(alert.name)="{ item }">
              <b-link :to="`/alerts/${item.alert_id}`">
                {{ item.alert_name }}
              </b-link>
            </template>
          </b-table>
          <b-card-footer v-if="hasMoreEvents" @click="showMore">
            <center>
              <a>Show More</a>
            </center>
          </b-card-footer>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<style scoped></style>
